import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../../api';
import { Gravador, type EstadoDoGravador } from '../../audio/gravador';
import { assinarFluxo, socketDoPainel } from '../../socket/painel';
import { BotaoIcone } from '../layout';
import { IconeEsperando, IconeMicrofone } from '../icones';
import { guardarSegmento, inserirNoCursor, juntarSegmentos, type SegmentoDeDitado } from './ditado';

/**
 * Ditado por voz para o compositor.
 *
 * Reusa a esteira de transcrição inteira — o mesmo `Gravador` da tela de
 * áudio, os mesmos trechos em WAV 16 kHz, o mesmo executor na fila — e muda só
 * o destino: em vez de virar uma gravação para consultar depois, o texto cai
 * no campo e a gravação é apagada.
 *
 * Duas decisões que economizam a VPS: a tomada contínua fica desligada (sem
 * ela o `finalizar` não enfileira o ffmpeg do NORMALIZAR), e no fim vem um
 * DELETE — senão cada frase ditada viraria uma gravação permanente na lista de
 * áudio, com objetos que ninguém apaga.
 *
 * O texto NÃO aparece palavra a palavra: o cortador só fecha um trecho depois
 * de dez segundos de áudio ou de uma pausa, e o provedor não tem streaming.
 * Uma frase curta vira um trecho só, transcrito quando se solta o botão. O
 * rótulo diz isso enquanto espera, em vez de fingir que travou.
 */
export function BotaoDeDitado({
  aoDitar,
  bloqueado,
  miudo = false,
}: {
  /** Recebe o texto pronto; quem chama decide onde encaixar. */
  aoDitar: (texto: string) => void;
  /** Motivo para não poder ditar agora (sessão morta, papel sem comando). */
  bloqueado?: string;
  /** Barra recolhida: o microfone fica pequeno mesmo no dedo. */
  miudo?: boolean;
}): React.JSX.Element | null {
  const [estado, setEstado] = useState<EstadoDoGravador | null>(null);
  const [esperando, setEsperando] = useState(false);
  const [erro, setErro] = useState('');
  const gravador = useRef<Gravador | null>(null);
  const segmentos = useRef<SegmentoDeDitado[]>([]);
  const gravacaoId = useRef('');
  const cursor = useRef(0);
  const cancelarAssinatura = useRef<(() => void) | null>(null);
  /**
   * O ouvinte exato que foi registrado, para poder tirar exatamente ele.
   *
   * Ele nasce dentro de `comecar` e quem precisa remove-lo e `terminar` --
   * outra funcao, outro escopo. Era por isso que o `off` era chamado SEM o
   * handler, e `off` sem handler remove TODOS os ouvintes do evento, inclusive
   * os de outra tela aberta noutra aba do painel.
   */
  const ouvinte = useRef<((s: SegmentoDeDitado & { fluxo?: string }) => void) | null>(null);

  // O item Áudio do menu some quando o servidor está sem provedor de
  // transcrição; o microfone segue o mesmo critério, pelo mesmo motivo.
  const { data: eu } = useQuery({
    queryKey: ['eu'],
    queryFn: () => chamar<{ recursos?: { transcricao?: boolean } }>(ROTAS.auth.eu),
    staleTime: 5 * 60_000,
  });

  // Sair da tela no meio de um ditado tem de desligar o microfone: uma luz
  // vermelha acesa depois de a pessoa fechar a conversa é falha grave.
  useEffect(
    () => () => {
      const vivo = gravador.current;
      gravador.current = null;
      void vivo?.parar().catch(() => undefined);
    },
    [],
  );

  if (eu && eu.recursos?.transcricao === false) return null;
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return (
      <BotaoIcone rotulo="Ditar" miudo={miudo} disabled title="este navegador não dá acesso ao microfone">
        <IconeMicrofone />
      </BotaoIcone>
    );
  }

  const gravando = Boolean(estado?.gravando);

  async function comecar(): Promise<void> {
    setErro('');
    segmentos.current = [];
    const socket = socketDoPainel();
    const motor = new Gravador((novo) => setEstado(novo), { semTomadaContinua: true });
    gravador.current = motor;

    const aoChegar = (segmento: SegmentoDeDitado & { fluxo?: string }): void => {
      // O socket é um só para o painel inteiro: sem filtrar pelo fluxo, o
      // ditado colheria segmento de qualquer gravação aberta em outra aba.
      if (segmento.fluxo && segmento.fluxo !== 'gravacao:' + gravacaoId.current) return;
      if (segmento.seq > cursor.current) cursor.current = segmento.seq;
      segmentos.current = guardarSegmento(segmentos.current, segmento);
    };

    try {
      const id = await motor.iniciar('Ditado no painel');
      gravacaoId.current = id;
      cursor.current = 0;
      ouvinte.current = aoChegar;
      socket.on('transcricao.segmento', aoChegar);
      cancelarAssinatura.current = assinarFluxo('gravacao:' + id, () => cursor.current);
    } catch (falha) {
      gravador.current = null;
      setEstado(null);
      socket.off('transcricao.segmento', aoChegar);
      // O nome do erro do navegador entra na frase: "não consegui abrir o
      // microfone" sozinho não diz se falta permissão, aparelho ou suporte, e
      // é justamente essa palavra que resolve o chamado.
      const nome = (falha as Error).name;
      setErro(
        nome === 'NotAllowedError'
          ? 'o navegador negou o microfone para este site'
          : nome === 'NotFoundError'
            ? 'nenhum microfone encontrado nesta máquina'
            : 'não consegui abrir o microfone (' + (nome || 'erro') + ')',
      );
    }
  }

  async function terminar(): Promise<void> {
    const motor = gravador.current;
    if (!motor) return;
    gravador.current = null;
    setEsperando(true);
    const id = gravacaoId.current;
    const socket = socketDoPainel();

    try {
      await motor.parar();
      // O último trecho só é transcrito depois do `finalizar`. Espera curta e
      // com teto: melhor entregar o que veio do que travar o campo.
      for (let tentativa = 0; tentativa < 20; tentativa += 1) {
        const pagina = await chamar<{ itens: SegmentoDeDitado[] }>(
          `${ROTAS.gravacoes}/${id}/segmentos?limite=200`,
        );
        for (const item of pagina.itens) segmentos.current = guardarSegmento(segmentos.current, item);
        const sessao = await chamar<{ estado: string }>(`${ROTAS.gravacoes}/${id}`);
        if (sessao.estado === 'CONCLUIDA' || sessao.estado === 'ERRO') break;
        await new Promise((r) => setTimeout(r, 700));
      }

      const texto = juntarSegmentos(segmentos.current);
      if (texto) aoDitar(texto);
      else setErro('não entendi nada do que foi dito');
    } catch {
      setErro('a transcrição não voltou');
    } finally {
      // COM o handler, e so o nosso.
      if (ouvinte.current) socket.off('transcricao.segmento', ouvinte.current);
      ouvinte.current = null;
      cancelarAssinatura.current?.();
      cancelarAssinatura.current = null;
      // Ditado não é acervo: a gravação sai da lista de áudio.
      void chamar(`${ROTAS.gravacoes}/${id}`, { method: 'DELETE' }).catch(() => undefined);
      setEsperando(false);
      setEstado(null);
      segmentos.current = [];
    }
  }

  // `||`, não `??`: string vazia não é nulo, e com `??` o título do botão
  // ficava vazio sempre que não havia erro — um ícone sem nenhuma dica.
  const motivo =
    bloqueado ||
    erro ||
    (esperando ? 'transcrevendo o que foi dito…' : gravando ? 'Parar de ditar' : 'Ditar');

  return (
    <span className="relative flex items-center gap-1.5">
      <BotaoIcone
        rotulo={gravando ? 'Parar de ditar' : 'Ditar'}
        title={motivo}
        ligado={gravando}
        miudo={miudo}
        disabled={Boolean(bloqueado) || esperando}
        onClick={() => void (gravando ? terminar() : comecar())}
      >
        {esperando ? <IconeEsperando className="animate-pulse" /> : <IconeMicrofone />}
      </BotaoIcone>
      {gravando ? (
        <span
          aria-hidden
          className="pointer-events-none absolute right-8 top-0.5 h-2 w-2 rounded-full bg-red-500"
        />
      ) : null}
      {/*
        O estado fica ao lado do campo, não numa notificação.
        Antes, a única pista de que a transcrição estava em curso era o ícone
        virar reticências — e, quando ficava pronta, chegava um aviso do sistema
        ("Transcrição pronta") por uma gravação que o painel apaga em seguida.
        Aviso de sistema é para quem não está olhando a tela; quem acabou de
        soltar o botão do microfone está olhando exatamente para cá.
      */}
      {esperando ? (
        <span className="whitespace-nowrap text-xs text-zinc-500" aria-live="polite">
          Transcrevendo…
        </span>
      ) : null}
      {erro ? (
        <span className="max-w-[24ch] truncate text-xs text-amber-400" title={erro}>
          {erro}
        </span>
      ) : null}
    </span>
  );
}

/** O encaixe do texto ditado no campo, para quem tem o cursor à mão. */
export { inserirNoCursor };
